/* Copyright (c) 2017 Backpack Health LLC */

import { authorizedApiRequestV2 } from './ApiRequest';
import ApiErrorModel from '../models/ApiErrorModel';
import { iTrackable, iTrackableDefinition, iEditableObservation, iTrackableTerm, iHealthEntryTrackable, iObservation } from './apiModels/Trackable';
import { iDateTime } from '../models/DateTimeModel';

interface iTrackableTermsResponse {
  trackable_terms: {
    [id: string] : iTrackableTerm;
  };
}

interface iTrackableApiResponse extends iTrackableTermsResponse {
  data: iTrackable;
}
interface iHealthEntryTrackableApiResponse extends iTrackableTermsResponse {
  data: iHealthEntryTrackable[];
  trackable_options: {
    trackable_term_id: number;
    observation_count: number;
  }[];
}
interface iTrackableShortcutsApiResponse extends iTrackableTermsResponse {
  data: {
    observation_count: number;
    trackable_id: number;
    trackable_term_id: number;
    latest_observation: {
      date: iDateTime;
      summary: string;
    },
  }[];
}

const TrackablesService = {

  get(profileId, trackableId): Promise<iTrackableApiResponse> {
    return authorizedApiRequestV2('get', `profiles/${profileId}/trackables/${trackableId}`)
      .then(res => res.body)
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  update(profileId, trackableData): Promise<any> {
    const trackableId = trackableData.id;
    return authorizedApiRequestV2('put', `profiles/${profileId}/trackables/${trackableId}`)
      .send(trackableData)
      .then(res => res.body)
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  getAll(profileId): Promise<{ data: iTrackable[] } & iTrackableTermsResponse> {
    return authorizedApiRequestV2('get', `profiles/${profileId}/trackables`)
      .then(res => res.body)
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  getDefinition(profileId, definitionId): Promise<{ data: iTrackableDefinition } & iTrackableTermsResponse> {
    return authorizedApiRequestV2('get', `profiles/${profileId}/trackables/definition/${definitionId}`)
      .then(res => res.body)
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  disable(profileId, healthEntryId, trackableId) {
    return authorizedApiRequestV2('delete', `profiles/${profileId}/trackables/healthEntries/${healthEntryId}/entryTrackables`)
      .send({ trackable_term_id: trackableId })
      .then(() => ({}))
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  enable(profileId, healthEntryId, trackableTermIds: number[]): Promise<{ data: iHealthEntryTrackable & iTrackableTermsResponse }> {
    return authorizedApiRequestV2('post', `profiles/${profileId}/trackables/healthEntries/${healthEntryId}/entryTrackables`)
      .send({ trackable_term_id: trackableTermIds })
      .then((payload) => payload.body);
  },
  getAllHealthEntryTrackables(profileId): Promise<iHealthEntryTrackableApiResponse> {
    return authorizedApiRequestV2('get', `profiles/${profileId}/trackables/entryTrackables`)
      .then((payload) => payload.body);
  },
  getAllForHealthEntry(profileId, healthEntryId): Promise<iHealthEntryTrackableApiResponse> {
    return authorizedApiRequestV2('get', `profiles/${profileId}/trackables/healthEntries/${healthEntryId}/entryTrackables`)
      .then(res => {
        const correctedData = { ...res.body };

        correctedData.trackable_options = correctedData.trackable_options.trackable_term_ids.map(id => ({
          trackable_term_id: id,
          observation_count: 0,
        }));

        return correctedData;
      })
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  getShortcuts(profileId): Promise<iTrackableShortcutsApiResponse> {
    return authorizedApiRequestV2('get', `profiles/${profileId}/trackables/shortcuts`)
      .then(res => res.body)
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  getCurrentDefinition(profileId, trackableId): Promise<{ data: iTrackableDefinition } & iTrackableTermsResponse> {
    return authorizedApiRequestV2('get', `profiles/${profileId}/trackables/${trackableId}/definitions/current`)
      .then(res => {
        return res.body;
      })
      .catch(err => {
        throw new ApiErrorModel(err);
      });
  },
  createObservation(profileId, trackableId, observation: iEditableObservation): Promise<{ data: iObservation & iTrackableTermsResponse }> {
    return authorizedApiRequestV2('post', `profiles/${profileId}/trackables/${trackableId}/observation`)
      .send(observation)
      .then((payload) => {
        return payload.body.data;
      });
  },
  updateObservation(profileId, trackableId, observationId, observation: iEditableObservation): Promise<{ data: iObservation & iTrackableTermsResponse }> {
    return authorizedApiRequestV2('put', `profiles/${profileId}/trackables/${trackableId}/observation/${observationId}`)
      .send(observation)
      .then((payload) => payload.body.data);
  },
  deleteObservation(profileId, trackableId, observationId) {
    return authorizedApiRequestV2('delete', `profiles/${profileId}/trackables/${trackableId}/observation/${observationId}`)
      .then(() => {});
  },
  deleteTrackable(profileId, trackableId) {
    return authorizedApiRequestV2('delete', `profiles/${profileId}/trackables/${trackableId}`)
      .then(() => {});
  },
};

export default TrackablesService;
